import {
  Paging,
  Table,
  usePaging,
  useAsync,
  CustomFilter,
  Input,
  Form,
  Modal,
  EllipsisSpan,
} from '@maxtropy/components';
import {
  apiV2AreaDeviceRelationSavePost,
  apiV2AreaDeviceRelationListPost,
  apiV2AreaDeviceSelectPost,
} from '@maxtropy/device-customer-apis-v2';
import { Key, useCallback, useEffect, useMemo, useState } from 'react';
import { useRequest } from 'ahooks';
import { Cascader } from 'antd';
import { useQuery } from '@/utils/utils';
import { DeviceTypeTree, formatOptionData, getChildNodesByParentIds, adaptToDeviceTypeTree } from '../utils';
import { Device } from '@/api/device';
import { getDeviceTypeTree } from '@/api/deviceType';

interface searchParams {
  codeOrName?: string;
  typeId?: Array<Array<number>> | undefined;
}
interface Params {
  codeOrName?: string;
  typeId?: number[];
}
export type DeviceListItem = Partial<Omit<Device, 'rootCode' | 'deviceId'>>;
const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tags',
    ellipsis: { showTitle: true },
    render: (v?: string[]) => <EllipsisSpan value={v ? v.join(',') : '--'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

type modalProps = {
  open: boolean;
  cancelModal: () => void;
  areaId: number;
  updateFn?: () => void;
};

const UnitModal = ({ open, cancelModal, areaId, updateFn }: modalProps) => {
  const pagingInfo = usePaging();
  const [form] = Form.useForm();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [selectedRowKeys, setSelectedRowKeys] = useState<number[]>([]);
  const [searchParams, setSearchParams] = useState<Params>();

  // 本次新增的数量和 ID
  const [currentAddedCount, setCurrentAddedCount] = useState<number>();

  //获取所属类目数据
  const rawData = useAsync(getDeviceTypeTree);
  const deviceTypeData = useMemo<DeviceTypeTree | undefined>(() => {
    return rawData ? adaptToDeviceTypeTree(rawData) : undefined;
  }, [rawData]);

  const { data, isLoading }: { data?: DeviceListItem[]; isLoading?: boolean } = useQuery(
    useCallback(async () => {
      if (!deviceTypeData) return;
      const res = await apiV2AreaDeviceSelectPost({
        ...searchParams,
        page: pageOffset,
        size: pageSize,
      });
      if (res.total) setTotalCount(res.total);
      return res.list;
    }, [pageOffset, pageSize, searchParams, setTotalCount, deviceTypeData])
  );

  // 获取已关联的设备 ID 列表
  const { data: relationList } = useRequest(
    () =>
      apiV2AreaDeviceRelationListPost({
        id: areaId,
      }).then(res => {
        return res.list || [];
      }),
    {
      refreshDeps: [areaId, open],
    }
  );

  // 记录初始选中的设备 ID
  const initialSelectedSet = useMemo(
    () => new Set((relationList ?? []).map(item => item.deviceId!).filter(id => id !== undefined)),
    [relationList]
  );
  // 当弹窗打开且 relationList 变化时，同步选中状态
  useEffect(() => {
    if (open) {
      const initialIds = (relationList ?? []).map(item => item.deviceId!).filter(id => id !== undefined);
      setSelectedRowKeys(initialIds);
      setCurrentAddedCount(0);
      setPageOffset(1);
    }
  }, [open, relationList]);

  // 监听选中状态变化，计算新增数量
  useEffect(() => {
    const newAddedIds = selectedRowKeys.filter(id => !initialSelectedSet.has(id));
    setCurrentAddedCount(newAddedIds.length);
  }, [selectedRowKeys, initialSelectedSet]);

  const onReset = () => {
    setSearchParams({});
    setPageOffset(1);
  };

  const handleCancel = () => {
    form.resetFields();
    setSearchParams({});
    setPageOffset(1);
    cancelModal();
  };

  const onFinish = (values: searchParams) => {
    const typeId = getChildNodesByParentIds(values.typeId, deviceTypeData?.tree);
    const params: Params = {
      codeOrName: values.codeOrName,
      typeId: typeId,
    };
    setSearchParams(params);
    setPageOffset(1);
  };

  const onSelectChange = (rowKeys: Key[]) => {
    const numericKeys = rowKeys.map(key => Number(key));
    setSelectedRowKeys(numericKeys);
  };
  const rowSelection = {
    preserveSelectedRowKeys: true,
    selectedRowKeys,
    getCheckboxProps: (record: DeviceListItem) => {
      const isInitial = initialSelectedSet.has(record.id as number);
      return {
        disabled: isInitial,
        name: record.name,
      };
    },
    onChange: onSelectChange,
  };
  const handleOk = () => {
    apiV2AreaDeviceRelationSavePost({
      areaId,
      deviceIds: selectedRowKeys,
    }).then(() => {
      updateFn?.();
      cancelModal();
    });
  };

  return (
    <Modal title="关联设备" open={open} width={1200} onOk={handleOk} onCancel={handleCancel}>
      <CustomFilter form={form} onFinish={val => onFinish(val as searchParams)} colSpan={8} onReset={onReset}>
        <Form.Item name="codeOrName" label="编号/名称">
          <Input placeholder={'请输入编号或名称查询'} />
        </Form.Item>
        <Form.Item name="typeId" label="所属类目">
          <Cascader
            options={formatOptionData(deviceTypeData)}
            allowClear={false}
            fieldNames={{ children: 'child' }}
            multiple
            maxTagCount="responsive"
            showSearch={{
              filter: (inputValue, path) =>
                path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1),
            }}
            placeholder={'请选择所属类目'}
          />
        </Form.Item>
      </CustomFilter>
      <Table
        scroll={{ y: 380 }}
        rowKey="id"
        loading={isLoading}
        rowSelection={rowSelection}
        columns={columns}
        dataSource={data}
        bottomLeftExtra={
          <div>
            <span>本次新增勾选</span>
            <span style={{ color: '#E64242' }}>{currentAddedCount}</span>
            <span>项，</span>
            <span>总计</span>
            <span style={{ color: '#E64242' }}>{selectedRowKeys.length}</span>
            <span>项</span>
          </div>
        }
      >
        <Paging pagingInfo={pagingInfo} />
      </Table>
    </Modal>
  );
};

export default UnitModal;
